/**
 * AnalyticsTab — Phase 4 MRR/ARR/churn revenue dashboard.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DollarSign, TrendingUp, Users, AlertTriangle, Download } from 'lucide-react';
import { superAdminApi } from './superAdminApi';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AnalyticsTab = () => {
  const { data, isLoading } = useQuery({
    queryKey: ['superadmin-analytics'],
    queryFn: () => superAdminApi.request?.('get', '/analytics/revenue') ||
                   (async () => {
                     const { default: axios } = await import('axios');
                     const token = getToken();
                     const res = await axios.get(`${API_URL}/api/superadmin/analytics/revenue`, { headers: { Authorization: `Bearer ${token}` } });
                     return res.data;
                   })(),
    refetchInterval: 60000,
  });

  const downloadAudit = async () => {
    const token = getToken();
    const res = await fetch(`${API_URL}/api/superadmin/audit/export?limit=10000`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `platform_audit_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  if (isLoading) return <p className="text-slate-400 p-8">Loading analytics...</p>;
  const d = data || {};
  const maxRev = Math.max(1, ...(d.revenue_by_month || []).map(m => m.revenue || 0));

  return (
    <div className="space-y-6" data-testid="superadmin-analytics-tab">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-white">Revenue Analytics</h3>
        <Button onClick={downloadAudit} size="sm" variant="outline"
                className="text-white border-white/20 bg-white/5 hover:bg-white/10" data-testid="audit-export-btn">
          <Download className="h-4 w-4 mr-1" /> Export Audit CSV
        </Button>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI icon={DollarSign} label="MRR" value={`$${d.mrr ?? 0}`} accent="emerald" testid="kpi-mrr" />
        <KPI icon={TrendingUp} label="ARR" value={`$${d.arr ?? 0}`} accent="indigo" testid="kpi-arr" />
        <KPI icon={Users} label="Active Subs"
             value={`${d.active_subscribers ?? 0}`}
             sub={`+${d.trial_subscribers ?? 0} trial`}
             accent="sky" testid="kpi-active-subs-analytics" />
        <KPI icon={AlertTriangle} label="Churn Rate"
             value={`${d.churn_rate_percent ?? 0}%`}
             sub={`${d.churned_30d ?? 0} churned in 30d`}
             accent="rose" testid="kpi-churn" />
      </div>

      {/* Revenue trend */}
      <Card className="bg-white/5 backdrop-blur-xl border-white/10 text-white">
        <CardHeader>
          <CardTitle className="text-base">Revenue — Last 6 Months</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-3 h-48">
            {(d.revenue_by_month || []).map(m => (
              <div key={m.month} className="flex-1 flex flex-col items-center gap-1">
                <div className="text-xs font-bold">${m.revenue}</div>
                <div className="bg-gradient-to-t from-indigo-600 to-fuchsia-500 rounded-t w-full min-h-[4px]"
                     style={{ height: `${(m.revenue / maxRev) * 100 || 2}%` }} />
                <div className="text-[10px] text-slate-500">{m.month}</div>
                <div className="text-[10px] text-slate-600">{m.invoice_count} inv</div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Top revenue tenants */}
      <Card className="bg-white/5 backdrop-blur-xl border-white/10 text-white">
        <CardHeader>
          <CardTitle className="text-base">Top Revenue Tenants (All Time)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {(d.top_tenants_by_revenue || []).map((t, i) => (
              <div key={t.tenant_id} className="flex items-center gap-3 p-2 rounded bg-white/5">
                <span className="text-xs font-mono text-slate-500 w-6">#{i + 1}</span>
                <div className="flex-1">
                  <p className="font-semibold text-sm">{t.name}</p>
                  <p className="text-xs text-slate-500 font-mono">{t.code}</p>
                </div>
                <Badge className="bg-emerald-500/20 text-emerald-200 border-emerald-400/30">${t.total_revenue}</Badge>
                <span className="text-xs text-slate-500">{t.invoice_count} inv</span>
              </div>
            ))}
            {(d.top_tenants_by_revenue || []).length === 0 && <p className="text-sm text-slate-500">No paid invoices yet.</p>}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const KPI = ({ icon: Icon, label, value, sub, accent, testid }) => {
  const iconBg = {
    emerald: 'bg-emerald-500/20 border-emerald-400/30 text-emerald-300',
    indigo: 'bg-indigo-500/20 border-indigo-400/30 text-indigo-300',
    sky: 'bg-sky-500/20 border-sky-400/30 text-sky-300',
    rose: 'bg-rose-500/20 border-rose-400/30 text-rose-300',
  }[accent] || 'bg-slate-500/20 border-slate-400/30 text-slate-300';
  return (
    <Card className="bg-white/5 backdrop-blur-xl border-white/10 text-white" data-testid={testid}>
      <CardContent className="pt-5 pb-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-400">{label}</p>
            <p className="text-3xl font-bold mt-1">{value}</p>
            {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
          </div>
          <div className={`w-11 h-11 rounded-xl border flex items-center justify-center ${iconBg}`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
